"use client";

import React from "react";
import { useFormStatus } from "react-dom";
import TimezonePicker from "@/app/components/TimezonePicker";
import PasswordRequirements, {
  firstPolicyError,
} from "@/app/components/PasswordRequirements";
import { signUpAndProvision } from "./actions";

type ActionState = { ok: true } | { ok: false; error: string };
const initialState: ActionState = { ok: false, error: "" };

const CTA_GREEN = "#0dfe00";

function getActionError(state: ActionState): string | null {
  return state.ok ? null : state.error;
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="text-sm font-medium text-neutral-300">{label}</label>
      {children}
    </div>
  );
}

function Input(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      className={[
        "mt-1 w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2",
        "text-neutral-200 placeholder:text-neutral-500",
        "focus:outline-none focus:ring-2 focus:ring-white/20",
        props.className ?? "",
      ].join(" ")}
    />
  );
}

function Spinner() {
  return (
    <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" aria-hidden="true">
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="3"
        fill="none"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8v3a5 5 0 00-5 5H4z"
      />
    </svg>
  );
}

function SubmitButton({ disabled }: { disabled: boolean }) {
  const { pending } = useFormStatus();
  const isDisabled = pending || disabled;

  return (
    <button
      type="submit"
      disabled={isDisabled}
      aria-disabled={isDisabled}
      className="flex w-full items-center justify-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold text-neutral-950 shadow-sm transition hover:brightness-95 focus:outline-none focus:ring-2 focus:ring-white/30 disabled:opacity-60 disabled:cursor-not-allowed"
      style={{ backgroundColor: CTA_GREEN }}
    >
      {pending ? (
        <>
          <Spinner />
          <span>Creating your account…</span>
        </>
      ) : (
        "Create account"
      )}
    </button>
  );
}

export default function SignupForm({
  lockedReferralSource,
}: {
  lockedReferralSource?: string | null;
}) {
  const [state, action] = React.useActionState(signUpAndProvision, initialState);

  const [password, setPassword] = React.useState("");
  const [confirm, setConfirm] = React.useState("");
  const [clientError, setClientError] = React.useState<string | null>(null);

  const [referralSource, setReferralSource] = React.useState(
    lockedReferralSource ?? "None"
  );
  const [referralName, setReferralName] = React.useState("");

  const isReferralLocked = Boolean(lockedReferralSource);

  const mismatch = confirm.length > 0 && password !== confirm;
  const policyErr = firstPolicyError(password);
  const canSubmit = !policyErr && !mismatch;

  const actionError = getActionError(state);
  const errorMessage = clientError ?? actionError;

  return (
    <form
      className="space-y-4"
      action={async (formData) => {
        // Client-side gate for instant feedback (server still validates too)
        const pw = String(formData.get("password") ?? "");
        const cf = String(formData.get("confirm_password") ?? "");

        const pe = firstPolicyError(pw);
        if (pe) {
          setClientError(pe);
          return;
        }
        if (pw !== cf) {
          setClientError("Passwords do not match.");
          return;
        }

        setClientError(null);
        await action(formData);
      }}
    >
      {errorMessage ? (
        <div className="rounded-xl border border-red-500/20 bg-red-500/10 p-3 text-sm text-red-200 break-words">
          {errorMessage}
        </div>
      ) : null}

      <div className="grid gap-4 md:grid-cols-2">
        <div className="md:col-span-2">
          <Field label="Organization name">
            <Input
              name="workspace_name"
              required
              placeholder="Acme Manufacturing"
              autoComplete="organization"
              onChange={() => setClientError(null)}
            />
          </Field>
        </div>

        <div className="md:col-span-2">
          <div className="rounded-xl border border-white/10 bg-white/5 p-3">
            <TimezonePicker
              name="timezone"
              defaultValue="America/Denver"
              description="Used for due dates and notification timing."
              variant="dark"
            />
          </div>
        </div>

        <div className="md:col-span-2">
          <Field label="How did you hear about us?">
            <select
              name="referral_source"
              value={referralSource}
              disabled={isReferralLocked}
              onChange={(e) => {
                setClientError(null);
                setReferralSource(e.target.value);
                if (e.target.value !== "Referral") setReferralName("");
              }}
              className="mt-1 w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-neutral-200 focus:outline-none focus:ring-2 focus:ring-white/20 disabled:cursor-not-allowed disabled:opacity-70"
            >
              <option value="None">None</option>
              <option value="TITANSofCNC">TITANSofCNC</option>
              <option value="YouTube">YouTube</option>
              <option value="Instagram">Instagram</option>
              <option value="Facebook">Facebook</option>
              <option value="Google">Google</option>
              <option value="Tradeshow">Tradeshow</option>
              <option value="Referral">Referral</option>
            </select>

            {isReferralLocked ? (
              <input type="hidden" name="referral_source" value={referralSource} />
            ) : null}

            {isReferralLocked ? (
              <p className="mt-1 text-xs text-neutral-500">
                Referral source applied from partner link.
              </p>
            ) : null}
          </Field>
        </div>

        {referralSource === "Referral" ? (
          <div className="md:col-span-2">
            <Field label="Referral name">
              <Input
                name="referral_name"
                required
                value={referralName}
                maxLength={24}
                pattern="[A-Za-z ]{1,24}"
                placeholder="Name"
                onChange={(e) => {
                  setClientError(null);
                  setReferralName(e.target.value.replace(/[^A-Za-z ]/g, ""));
                }}
              />
            </Field>
          </div>
        ) : null}

        <div className="md:col-span-2">
          <Field label="Email">
            <Input
              name="email"
              type="email"
              required
              autoComplete="email"
              onChange={() => setClientError(null)}
            />
          </Field>
        </div>

        <div>
          <Field label="Password">
            <Input
              name="password"
              type="password"
              required
              autoComplete="new-password"
              placeholder="Create a password"
              value={password}
              onChange={(e) => {
                setClientError(null);
                setPassword(e.target.value);
              }}
            />
          </Field>
        </div>

        <div>
          <Field label="Confirm password">
            <Input
              name="confirm_password"
              type="password"
              required
              autoComplete="new-password"
              placeholder="Re-enter password"
              className={mismatch ? "border-red-500/30 focus:ring-red-500/20" : ""}
              value={confirm}
              onChange={(e) => {
                setClientError(null);
                setConfirm(e.target.value);
              }}
            />
          </Field>
        </div>

        <div className="md:col-span-2">
          <PasswordRequirements password={password} />
        </div>

        {mismatch ? (
          <div className="md:col-span-2 text-sm text-red-200">
            Passwords do not match.
          </div>
        ) : null}

        <div className="md:col-span-2">
          <Field label="Full name">
            <Input
              name="full_name"
              required
              autoComplete="name"
              onChange={() => setClientError(null)}
            />
          </Field>
        </div>

        <div className="md:col-span-2">
          <Field label="Address line 1">
            <Input
              name="address_line1"
              required
              autoComplete="address-line1"
              onChange={() => setClientError(null)}
            />
          </Field>
        </div>

        <div className="md:col-span-2">
          <Field label="Address line 2">
            <Input
              name="address_line2"
              autoComplete="address-line2"
              onChange={() => setClientError(null)}
            />
          </Field>
        </div>

        <div>
          <Field label="City">
            <Input
              name="city"
              required
              autoComplete="address-level2"
              onChange={() => setClientError(null)}
            />
          </Field>
        </div>

        <div>
          <Field label="State">
            <Input
              name="state"
              required
              autoComplete="address-level1"
              onChange={() => setClientError(null)}
            />
          </Field>
        </div>

        <div>
          <Field label="Postal code">
            <Input
              name="postal_code"
              required
              autoComplete="postal-code"
              onChange={() => setClientError(null)}
            />
          </Field>
        </div>

        <div>
          <Field label="Country">
            <Input
              name="country"
              required
              placeholder="USA"
              autoComplete="country-name"
              onChange={() => setClientError(null)}
            />
          </Field>
        </div>
      </div>

      <SubmitButton disabled={!canSubmit} />

      <div className="text-xs text-neutral-500">
        You’ll confirm your email next, then complete payment to activate your workspace.
      </div>
    </form>
  );
}
